import React, { useState, useCallback, useMemo } from 'react';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import PinnedQueriesEditor from './PinnedQueriesEditor';
import SuggestionsEditor from './SuggestionsEditor';
import CategoriesEditor from './CategoriesEditor';
import ProductsEditor from './ProductsEditor';
import CollapsibleEditorSection from './CollapsibleEditorSection';
import styles from './SettingsForm.module.scss';

/**
 * Controlled empty-query editor layout.
 *
 * The page container owns draft, dirty state, and page actions; this component
 * only renders the collapsible editors and emits value changes.
 */
const SettingsForm = ({
  terms = [],
  excludedTerms = [],
  productLimit = 16,
  words = [],
  excludedWords = [],
  categories = [],
  excludedCategoryIds = [],
  products = [],
  excludedProductIds = [],
  autoFillProducts = true,
  autoFillTerms = true,
  autoFillCategories = true,
  autoFillWords = true,
  onTermsChange,
  onExcludedTermsChange = () => {},
  onWordsChange,
  onExcludedWordsChange = () => {},
  onCategoriesChange,
  onExcludedCategoryIdsChange = () => {},
  onProductsChange,
  onExcludedProductIdsChange = () => {},
  onAutoFillProductsChange = () => {},
  onAutoFillTermsChange = () => {},
  onAutoFillCategoriesChange = () => {},
  onAutoFillWordsChange = () => {},
  generatedTerms = [],
  generatedWords = [],
  generatedCategories = [],
  generatedProducts = [],
  knownCategories = [],
  knownProducts = [],
  onSearchCategories,
  onSearchProducts,
  onProductLimitChange = () => {},
}) => {
  const t = useLocalizedMessage();

  const [openSection, setOpenSection] = useState('products');

  const toggleSection = useCallback((key) => {
    setOpenSection((current) => (current === key ? null : key));
  }, []);

  // Compute visible generated counts matching the editors' dedupe logic
  // so collapsed section headers show accurate numbers.
  const visibleGeneratedTermsCount = useMemo(() => {
    const pinnedLower = terms.map((p) => p.trim().toLowerCase());
    const excludedLower = excludedTerms.map((term) => term.trim().toLowerCase());
    return generatedTerms.filter((g) => {
      const key = g.term.trim().toLowerCase();
      return !pinnedLower.includes(key) && !excludedLower.includes(key);
    }).length;
  }, [terms, excludedTerms, generatedTerms]);

  const effectiveTermsCount = Math.min(
    10,
    terms.length + (autoFillTerms ? visibleGeneratedTermsCount : 0),
  );

  const effectiveWordsCount = useMemo(() => {
    const pinnedLower = words.map((w) => w.trim().toLowerCase());
    const excludedLower = excludedWords.map((w) => w.trim().toLowerCase());
    const generatedCount = generatedWords.filter((w) => {
      const key = String(w).trim().toLowerCase();
      return !pinnedLower.includes(key) && !excludedLower.includes(key);
    }).length;
    return Math.min(20, words.length + (autoFillWords ? generatedCount : 0));
  }, [autoFillWords, excludedWords, generatedWords, words]);

  const effectiveCategoriesCount = useMemo(() => {
    const pinnedIds = new Set(categories.map((c) => `${c.id}`));
    const excludedIds = new Set(excludedCategoryIds.map((id) => `${id}`));
    const generatedCount = generatedCategories.filter((c) => (
      !pinnedIds.has(`${c.id}`) && !excludedIds.has(`${c.id}`)
    )).length;
    return Math.min(6, categories.length + (autoFillCategories ? generatedCount : 0));
  }, [autoFillCategories, categories, excludedCategoryIds, generatedCategories]);

  const effectiveProductsCount = useMemo(() => {
    const pinnedIds = new Set(products.map((p) => `${p.id}`));
    const excludedIds = new Set(excludedProductIds.map((id) => `${id}`));
    const generatedCount = generatedProducts.filter((p) => (
      !pinnedIds.has(`${p.id}`) && !excludedIds.has(`${p.id}`)
    )).length;
    return Math.min(
      productLimit,
      products.length + (autoFillProducts ? generatedCount : 0),
    );
  }, [autoFillProducts, excludedProductIds, generatedProducts, productLimit, products]);

  return (
    <div className={styles.sections} data-testid="empty-query-sections">
      <CollapsibleEditorSection
        testId="empty-query-section-products"
        title={t('empty.query.products.editor.title')}
        subtitle={t('empty.query.products.editor.subtitle')}
        meta={autoFillProducts
          ? t('empty.query.collection.meta', {
            count: effectiveProductsCount,
            limit: productLimit,
          })
          : t('empty.query.collection.meta.selected', { count: effectiveProductsCount })}
        isOpen={openSection === 'products'}
        onToggle={() => toggleSection('products')}
        tone="products"
      >
        <ProductsEditor
          pinnedProducts={products}
          generatedProducts={generatedProducts}
          resolvedProducts={knownProducts}
          excludedProductIds={excludedProductIds}
          onSearch={onSearchProducts}
          onChange={onProductsChange}
          onExcludedChange={onExcludedProductIdsChange}
          limit={productLimit}
          onLimitChange={onProductLimitChange}
          autoFill={autoFillProducts}
          onAutoFillChange={onAutoFillProductsChange}
          hideHeader
        />
      </CollapsibleEditorSection>

      <CollapsibleEditorSection
        testId="empty-query-section-categories"
        title={t('empty.query.categories.editor.title')}
        subtitle={t('empty.query.categories.editor.subtitle')}
        meta={autoFillCategories
          ? t('empty.query.collection.meta', {
            count: effectiveCategoriesCount,
            limit: 6,
          })
          : t('empty.query.collection.meta.selected', { count: effectiveCategoriesCount })}
        isOpen={openSection === 'categories'}
        onToggle={() => toggleSection('categories')}
        tone="categories"
      >
        <CategoriesEditor
          pinnedCategories={categories}
          generatedCategories={generatedCategories}
          resolvedCategories={knownCategories}
          excludedCategoryIds={excludedCategoryIds}
          onSearch={onSearchCategories}
          onChange={onCategoriesChange}
          onExcludedChange={onExcludedCategoryIdsChange}
          autoFill={autoFillCategories}
          onAutoFillChange={onAutoFillCategoriesChange}
          hideHeader
        />
      </CollapsibleEditorSection>

      <CollapsibleEditorSection
        testId="empty-query-section-queries"
        title={t('empty.query.editor.title')}
        subtitle={t('empty.query.editor.subtitle')}
        meta={autoFillTerms
          ? t('empty.query.collection.meta', { count: effectiveTermsCount, limit: 10 })
          : t('empty.query.collection.meta.selected', { count: effectiveTermsCount })}
        isOpen={openSection === 'queries'}
        onToggle={() => toggleSection('queries')}
        tone="queries"
      >
        <PinnedQueriesEditor
          pinnedTerms={terms}
          excludedTerms={excludedTerms}
          generatedTerms={generatedTerms}
          onChange={onTermsChange}
          onExcludedTermsChange={onExcludedTermsChange}
          autoFill={autoFillTerms}
          onAutoFillChange={onAutoFillTermsChange}
          hideHeader
        />
      </CollapsibleEditorSection>

      <CollapsibleEditorSection
        testId="empty-query-section-suggestions"
        title={t('empty.query.words.editor.title')}
        subtitle={t('empty.query.words.editor.subtitle')}
        meta={autoFillWords
          ? t('empty.query.collection.meta', { count: effectiveWordsCount, limit: 20 })
          : t('empty.query.collection.meta.selected', { count: effectiveWordsCount })}
        isOpen={openSection === 'suggestions'}
        onToggle={() => toggleSection('suggestions')}
        tone="suggestions"
      >
        <SuggestionsEditor
          pinnedWords={words}
          excludedWords={excludedWords}
          generatedWords={generatedWords}
          onChange={onWordsChange}
          onExcludedChange={onExcludedWordsChange}
          autoFill={autoFillWords}
          onAutoFillChange={onAutoFillWordsChange}
          hideHeader
        />
      </CollapsibleEditorSection>
    </div>
  );
};

export default SettingsForm;
